import { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Container,
  Stack,
  Divider,
  CircularProgress // 👈 ADDED
} from "@mui/material";

// Icons
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { api } from "../api/api";

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [ordersToday, setOrdersToday] = useState(0);

  // 👈 ADDED: Tracks which specific order is currently being marked ready
  const [loadingOrderId, setLoadingOrderId] = useState(null);

  const vendorId = localStorage.getItem("vendor_id");

  // ✅ LOAD LIVE DASHBOARD DATA (Wrapped in useCallback)
  const loadData = useCallback(async () => {
    try {
      const [ordersRes, finRes] = await Promise.all([
        api.get(`/vendor/${vendorId}/orders`),
        api.get(`/vendor/${vendorId}/financial-summary?filter=today`)
      ]);
      setOrders(ordersRes.data);
      setOrdersToday(finRes.data.total_orders || 0);
    } catch (err) {
      console.log("Dashboard fetch error:", err);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) {
      loadData();
      // ✅ Poll every 5 seconds to keep Dashboard Live
      const interval = setInterval(loadData, 5000);
      return () => clearInterval(interval);
    }
  }, [vendorId, loadData]);

  // ✅ MARK READY LOGIC (UPDATED WITH LOADING STATE)
  const markReady = async (orderId) => {
    setLoadingOrderId(orderId); // 👈 Start loading spinner for this specific button
    try {
      await api.put("/vendor/order/ready", {
        order_id: orderId,
        vendor_id: Number(vendorId)
      });
      await loadData();
    } catch (err) {
      console.log("Error marking ready:", err);
    } finally {
      setLoadingOrderId(null); // 👈 Stop loading spinner
    }
  };

  // ✅ 2. FILTER PENDING ORDERS 
  const pendingOrders = orders.filter(
    (o) => o.status !== "DELIVERED" && o.status !== "CANCELLED" && !o.items.every((i) => i.status === "READY")
  );

  // ✅ 3. FILTER LIVE ORDERS 
  const liveOrders = orders.filter(
    (o) => o.status !== "DELIVERED" && o.status !== "CANCELLED" && o.items.every((i) => i.status === "READY")
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pt: { xs: 2, sm: 3 }, pb: 10, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="md" disableGutters>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Dashboard
          </Typography>
        </Box>

        <Box sx={{ mb: 5 }}>
          {/* ORDERS TODAY CARD */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: "24px",
              bgcolor: "#FFFFFF",
              border: "none",
              boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)",
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)",
              }
            }}
          >
            <Avatar sx={{ bgcolor: "#E1EED3", color: "#577A34", width: 64, height: 64 }}>
              <ReceiptLongIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                ORDERS TODAY
              </Typography>
              <Typography variant="h3" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.5, lineHeight: 1.1 }}>
                {ordersToday}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ========================================== */}
        {/* ✅ 2. PENDING ORDERS DROPDOWN              */}
        {/* ========================================== */}
        <Accordion 
          defaultExpanded 
          disableGutters 
          elevation={0}
          sx={{ 
            mb: 4, 
            borderRadius: "24px !important", 
            bgcolor: "transparent",
            '&:before': { display: 'none' } 
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: "#3A2C23", fontSize: '1.8rem' }}/>} 
            sx={{ 
              bgcolor: "#FFFFFF", 
              borderRadius: "20px",
              border: "1px solid #ECE5DA",
              boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
              px: { xs: 2, sm: 3 },
              py: 1.5,
              mb: 2 
            }}
          >
            <Typography variant="h6" fontWeight="900" color="#3A2C23">
              Pending Orders ({pendingOrders.length})
            </Typography>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0 }}>
            {pendingOrders.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#FFFFFF', borderRadius: '20px', border: '1px dashed #ECE5DA' }}>
                <Typography color="#A59D96" fontWeight="700">No pending orders right now.</Typography>
              </Box>
            ) : (
              pendingOrders.map((o) => (
                <Paper key={o.order_id} elevation={0} sx={{ 
                  mb: 2.5, 
                  p: { xs: 2.5, sm: 3 }, 
                  borderRadius: "24px", 
                  bgcolor: "#FFFFFF", 
                  border: "none", 
                  boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)" }
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                        ORDER ID
                      </Typography>
                      <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", lineHeight: 1.1 }}>
                        #{o.order_id}
                      </Typography>
                    </Box>
                    <Chip
                      label={o.status}
                      size="small"
                      sx={{ fontWeight: "800", bgcolor: "#FFF6DE", color: "#F29D17", borderRadius: '8px', px: 1 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

                  <Box sx={{ mb: 2.5 }}>
                    {o.items.map((i, idx) => (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" key={idx} sx={{ 
                        mb: 1.5, p: 1.5, bgcolor: '#FFFDF8', border: '1px solid #ECE5DA', borderRadius: '12px' 
                      }}>
                        <Box>
                          <Typography variant="body2" fontWeight="800" sx={{ color: '#2F2A25' }}>
                            {i.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6E655E', fontWeight: '700' }}>
                            Qty: {i.quantity}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight="900" sx={{ color: '#3A2C23' }}>
                            ₹{i.vendor_price * i.quantity}
                          </Typography>
                          <Typography variant="caption" sx={{ color: i.status === "READY" ? '#577A34' : '#F29D17', fontWeight: '800' }}>
                            {i.status || "PREPARING"}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Box>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                        TOTAL AMOUNT
                      </Typography>
                      <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.2 }}>
                        ₹{o.total_vendor_price}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => markReady(o.order_id)}
                      disabled={loadingOrderId === o.order_id} // 👈 ADDED disabled condition
                      sx={{
                        bgcolor: "#577A34",
                        color: "#FFFFFF",
                        fontWeight: "900",
                        borderRadius: "14px",
                        textTransform: "none",
                        px: 3,
                        py: 1.2,
                        boxShadow: "0 4px 12px rgba(87, 122, 52, 0.3)",
                        "&:hover": { bgcolor: "#4a692a" },
                        "&.Mui-disabled": { bgcolor: "#c5d1b7", color: "#FFFFFF" } // Keep it looking nice when disabled
                      }}
                    >
                      {/* 👈 ADDED LOADING SPINNER */}
                      {loadingOrderId === o.order_id ? (
                        <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
                      ) : (
                        "Mark Ready"
                      )}
                    </Button>
                  </Stack>
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>

        {/* ========================================== */}
        {/* ✅ 3. LIVE ORDERS DROPDOWN                 */}
        {/* ========================================== */}
        <Accordion 
          defaultExpanded 
          disableGutters 
          elevation={0}
          sx={{ 
            mb: 3, 
            borderRadius: "24px !important", 
            bgcolor: "transparent",
            '&:before': { display: 'none' } 
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: "#3A2C23", fontSize: '1.8rem' }}/>} 
            sx={{ 
              bgcolor: "#FFFFFF", 
              borderRadius: "20px",
              border: "1px solid #ECE5DA",
              boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
              px: { xs: 2, sm: 3 },
              py: 1.5,
              mb: 2
            }}
          >
            <Typography variant="h6" fontWeight="900" color="#3A2C23">
              Live Orders ({liveOrders.length})
            </Typography>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0 }}>
            {liveOrders.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#FFFFFF', borderRadius: '20px', border: '1px dashed #ECE5DA' }}>
                <Typography color="#A59D96" fontWeight="700">No live orders ready for pickup.</Typography>
              </Box>
            ) : (
              liveOrders.map((o) => (
                <Paper key={o.order_id} elevation={0} sx={{ 
                  mb: 2.5, 
                  p: { xs: 2.5, sm: 3 }, 
                  borderRadius: "24px", 
                  bgcolor: "#FFFFFF", 
                  border: "none", 
                  boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)" }
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                        ORDER ID
                      </Typography>
                      <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", lineHeight: 1.1 }}>
                        #{o.order_id}
                      </Typography>
                    </Box>
                    <Chip
                      label={o.status}
                      size="small"
                      sx={{ fontWeight: "800", bgcolor: "#FFF0EE", color: "#E85D3F", borderRadius: '8px', px: 1 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

                  <Box sx={{ mb: 2.5 }}>
                    {o.items.map((i, idx) => (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" key={idx} sx={{ 
                        mb: 1.5, p: 1.5, bgcolor: '#FFFDF8', border: '1px solid #ECE5DA', borderRadius: '12px' 
                      }}>
                        <Box>
                          <Typography variant="body2" fontWeight="800" sx={{ color: '#2F2A25' }}>
                            {i.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6E655E', fontWeight: '700' }}>
                            Qty: {i.quantity}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight="900" sx={{ color: '#3A2C23' }}>
                            ₹{i.vendor_price * i.quantity}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#577A34', fontWeight: '800' }}>
                            READY
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Box>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                        TOTAL AMOUNT
                      </Typography>
                      <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.2 }}>
                        ₹{o.total_vendor_price}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" sx={{ 
                      bgcolor: '#E1EED3', color: "#577A34", px: 2, py: 1, borderRadius: '12px', border: '1px solid #DDECC8' 
                    }}>
                      <CheckBoxIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                      <Typography fontWeight="900" variant="body2">
                        Ready for Pickup
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))
            )}
          </AccordionDetails>
        </Accordion>

      </Container>
    </Box>
  );
}